import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SavedParkingLotInput } from "@/modules/curb-zone/hooks/use-parking-lot";
import { Dispatch, SetStateAction } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";

import { Button } from "@/components/ui/button";
import { City } from "types";
import { Input } from "@/components/ui/input";
import { ParkingLot } from "services/web/api-parking-lot.type";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface ModifyParkingLotPromptProps {
  setOpenParkingLotModal: Dispatch<SetStateAction<boolean>>;
  parkingLotDetail?: ParkingLot;
  currentCity: City | null;
  resetEditState: VoidFunction;
  saveParkingLotProperties: (input: SavedParkingLotInput) => Promise<void>;
}

const formSchema = z.object({
  name: z.string().trim().min(1, { message: "Name is required" }),
  address: z.string().trim().min(1, { message: "Address is required" }),
  note: z.string().optional(),
});

export type ParkingLotProperties = z.infer<typeof formSchema>;

export const ModifyParkingLotPrompt = ({
  parkingLotDetail,
  saveParkingLotProperties,
  resetEditState,
}: ModifyParkingLotPromptProps) => {
  const form = useForm<ParkingLotProperties>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: parkingLotDetail?.name ?? "",
      address: parkingLotDetail?.address ?? "",
      note: parkingLotDetail?.note ?? "",
    },
    mode: "all",
  });

  const handleClose = () => {
    resetEditState();
    form.reset();
  };

  const submit = async (formValues: ParkingLotProperties) => {
    await saveParkingLotProperties({ promptedProperties: formValues });
    handleClose();
  };

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${parkingLotDetail?.id ? "Edit a" : "Add a new"} parking lot`}</DialogTitle>
          <DialogDescription>Parking lot information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Name<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Address<span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="sm:justify-between">
              <Button type="button" variant="secondary" onClick={handleClose}>
                Close
              </Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
